/**
 * MyResponsesView — candidate-facing "My Responses" (candidate-attribution
 * Phase 3, outreach MVP).
 *
 * A CANDIDATE user reviews the per-candidate leans recorded at the door for
 * THEIR OWN race — nothing else. The result is scoped server-side to the
 * candidate the caller is linked to; this view adds a local role gate as
 * defense-in-depth and only ever reads the candidate resolved from
 * `GET /candidates/mine`.
 *
 * Flow:
 *   - Role gate (CANDIDATE only) → "Access restricted" panel otherwise.
 *   - GET /candidates/mine on mount; the own race is the first row with
 *     relation === 'candidate' (typically exactly one). No candidate link →
 *     empty state.
 *   - GET /candidates/{id}/leans for that candidate. No leans → friendly empty
 *     state.
 *
 * Surface: candidate context card · summary strip (Support / Undecided /
 * Oppose / Refused buckets) · a table (desktop) or cards (mobile via
 * useIsMobile) of one row per lean, newest first (the backend already sorts).
 * Read-only — no PII export (candidates are not bulk-export).
 *
 * Pattern mirrors MyVolunteersView / LeadsView: axios + useState/useEffect,
 * semantic Tailwind tokens, ViewHeader, useIsMobile for structural swaps.
 * Strictly typed (no @ts-nocheck).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { MessageSquareText, ShieldAlert, MapPin } from 'lucide-react';
import { ViewHeader } from '@/components/ViewHeader';
import { Skeleton } from '@/components/Skeleton';
import EmptyState from '@/components/EmptyState';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { listMyCandidates, type MyCandidate } from '@/api/delegation';
import {
  getCandidateLeans,
  type CandidateLean,
  type CandidateLeanRow,
  type LeanSource,
} from '@/api/leans';

// ---------------------------------------------------------------------------
// Lean presentation
// ---------------------------------------------------------------------------

/** The four summary buckets a lean rolls up into. */
type LeanBucket = 'support' | 'undecided' | 'oppose' | 'refused';

const LEAN_META: Record<
  CandidateLean,
  { label: string; bucket: LeanBucket; pill: string }
> = {
  strong_support: {
    label: 'Strong support',
    bucket: 'support',
    pill: 'bg-status-success-bg/40 border-status-success-bg/50 text-status-success',
  },
  lean_support: {
    label: 'Lean support',
    bucket: 'support',
    pill: 'bg-status-success-bg/25 border-status-success-bg/40 text-status-success',
  },
  undecided: {
    label: 'Undecided',
    bucket: 'undecided',
    pill: 'bg-bg-elevated border-border-base text-fg-secondary',
  },
  lean_oppose: {
    label: 'Lean oppose',
    bucket: 'oppose',
    pill: 'bg-status-danger-bg/25 border-status-danger-bg/40 text-status-danger',
  },
  strong_oppose: {
    label: 'Strong oppose',
    bucket: 'oppose',
    pill: 'bg-status-danger-bg/40 border-status-danger-bg/50 text-status-danger',
  },
  refused: {
    label: 'Refused',
    bucket: 'refused',
    pill: 'bg-bg-elevated border-border-base text-fg-muted',
  },
};

const SOURCE_META: Record<LeanSource, { label: string; cls: string }> = {
  slate: {
    label: 'Slate',
    cls: 'bg-status-info-bg/30 border-status-info-bg/50 text-status-info',
  },
  unsolicited: {
    label: 'Unsolicited',
    cls: 'bg-bg-elevated border-border-base text-fg-muted',
  },
};

const BUCKET_ORDER: LeanBucket[] = ['support', 'undecided', 'oppose', 'refused'];

const BUCKET_META: Record<
  LeanBucket,
  { label: string; bar: string; text: string }
> = {
  support: { label: 'Support', bar: 'bg-status-success', text: 'text-status-success' },
  undecided: { label: 'Undecided', bar: 'bg-fg-muted', text: 'text-fg-secondary' },
  oppose: { label: 'Oppose', bar: 'bg-status-danger', text: 'text-status-danger' },
  refused: { label: 'Refused', bar: 'bg-border-subtle', text: 'text-fg-muted' },
};

function leanLabel(lean: CandidateLean): string {
  return LEAN_META[lean]?.label ?? lean;
}

function voterName(row: CandidateLeanRow): string {
  const { first_name, last_name } = row.voter;
  const name = [first_name, last_name].filter(Boolean).join(' ').trim();
  return name || 'Unknown voter';
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function errorDetail(err: unknown): string {
  const maybe = err as { response?: { data?: { detail?: unknown }; status?: number } };
  const detail = maybe?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  const status = maybe?.response?.status;
  if (status) return `Request failed (${status}). Please try again.`;
  return 'Could not reach the backend. Please try again.';
}

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

export interface MyResponsesViewProps {
  userRole?: string;
}

export function MyResponsesView({ userRole }: MyResponsesViewProps) {
  const roleNorm = typeof userRole === 'string' ? userRole.toUpperCase() : '';
  if (roleNorm !== 'CANDIDATE') {
    return (
      <div className="space-y-6">
        <ViewHeader
          title={
            <span className="flex items-center gap-2">
              <MessageSquareText className="w-6 h-6 text-accent" />
              My Responses
            </span>
          }
          tagline="Voter responses recorded for your race"
        />
        <div className="max-w-2xl rounded-lg border border-status-danger-bg/30 bg-status-danger-bg/20 p-6">
          <h2 className="text-lg font-semibold text-status-danger mb-2 flex items-center gap-2">
            <ShieldAlert className="w-5 h-5" aria-hidden="true" />
            Access restricted
          </h2>
          <p className="text-fg-secondary">
            My Responses is available to candidate accounts only. It shows the
            leans voters expressed toward a single candidate&apos;s race.
          </p>
        </div>
      </div>
    );
  }

  return <MyResponsesInner />;
}

function MyResponsesInner() {
  const isMobile = useIsMobile();

  const [candidate, setCandidate] = useState<MyCandidate | null>(null);
  const [rows, setRows] = useState<CandidateLeanRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setRows(null);

    (async () => {
      try {
        const mine = await listMyCandidates();
        if (cancelled) return;
        const own = mine.find((c) => c.relation === 'candidate') ?? null;
        setCandidate(own);
        if (!own) {
          setRows(null);
          setLoading(false);
          return;
        }
        const leans = await getCandidateLeans(own.candidate_id);
        if (cancelled) return;
        setRows(leans);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setLoadError(errorDetail(err));
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reload]);

  const summary = useMemo(() => {
    const counts: Record<LeanBucket, number> = {
      support: 0,
      undecided: 0,
      oppose: 0,
      refused: 0,
    };
    for (const row of rows ?? []) {
      const bucket = LEAN_META[row.lean]?.bucket;
      if (bucket) counts[bucket] += 1;
    }
    return counts;
  }, [rows]);

  const total = rows?.length ?? 0;

  return (
    <div className="space-y-6">
      <ViewHeader
        title={
          <span className="flex items-center gap-2">
            <MessageSquareText className="w-6 h-6 text-accent" />
            My Responses
          </span>
        }
        tagline="Leans voters expressed toward your race, recorded at the door"
      />

      {loadError && (
        <div className="bg-status-danger-bg/30 rounded-xl p-4 border border-status-danger-bg text-status-danger text-sm flex items-center justify-between gap-3">
          <span>{loadError}</span>
          <button
            type="button"
            onClick={() => setReload((n) => n + 1)}
            className="bg-accent hover:bg-accent-hover text-white px-3 py-1.5 rounded text-xs font-bold"
          >
            Retry
          </button>
        </div>
      )}

      {loading && (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-20" />
          <Skeleton className="h-64" />
        </div>
      )}

      {!loading && !loadError && !candidate && (
        <div className="max-w-2xl rounded-xl border border-border-base bg-bg-raised p-6">
          <h2 className="text-lg font-semibold text-fg-primary mb-2">
            No race linked to your account
          </h2>
          <p className="text-fg-secondary">
            Your account isn&apos;t linked to a candidate yet, so there are no
            responses to show. Ask your committee admin to connect you to your
            race.
          </p>
        </div>
      )}

      {!loading && !loadError && candidate && (
        <>
          <CandidateCard candidate={candidate} />

          {total === 0 ? (
            <EmptyState
              icon={MessageSquareText}
              title="No responses recorded for your race yet"
              description="As canvassers log door knocks for your candidacy, the voter leans they capture will appear here."
            />
          ) : (
            <>
              <SummaryStrip counts={summary} total={total} />
              {isMobile ? (
                <LeanCards rows={rows ?? []} />
              ) : (
                <LeanTable rows={rows ?? []} />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Candidate context card
// ---------------------------------------------------------------------------

function CandidateCard({ candidate }: { candidate: MyCandidate }) {
  return (
    <div className="rounded-xl border border-border-base bg-bg-raised p-5">
      <div className="text-[10px] uppercase tracking-wider text-accent font-semibold mb-1">
        Your race
      </div>
      <h2 className="text-xl font-bold text-fg-primary leading-tight">
        {candidate.full_name}
      </h2>
      <div className="text-fg-secondary text-sm mt-0.5">{candidate.office}</div>
      {candidate.district_label && (
        <div className="text-fg-muted text-sm">{candidate.district_label}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary strip
// ---------------------------------------------------------------------------

function SummaryStrip({
  counts,
  total,
}: {
  counts: Record<LeanBucket, number>;
  total: number;
}) {
  return (
    <div className="rounded-xl border border-border-base bg-bg-raised p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-fg-primary">Response summary</div>
        <div className="text-xs text-fg-muted tabular-nums">
          {total.toLocaleString()} total
        </div>
      </div>

      {/* Proportional bar */}
      <div
        className="flex h-2 w-full overflow-hidden rounded-full bg-bg-base mb-4"
        role="img"
        aria-label="Proportional breakdown of responses by bucket"
      >
        {BUCKET_ORDER.map((bucket) => {
          const n = counts[bucket];
          if (n === 0) return null;
          const pct = total > 0 ? (n / total) * 100 : 0;
          return (
            <div
              key={bucket}
              className={BUCKET_META[bucket].bar}
              style={{ width: `${pct}%` }}
              title={`${BUCKET_META[bucket].label}: ${n}`}
            />
          );
        })}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {BUCKET_ORDER.map((bucket) => (
          <div
            key={bucket}
            className="rounded-lg border border-border-base bg-bg-base px-3 py-2"
          >
            <div className="flex items-center gap-1.5">
              <span
                className={`inline-block w-2 h-2 rounded-full ${BUCKET_META[bucket].bar}`}
                aria-hidden="true"
              />
              <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
                {BUCKET_META[bucket].label}
              </span>
            </div>
            <div
              className={`text-2xl font-bold tabular-nums mt-0.5 ${BUCKET_META[bucket].text}`}
            >
              {counts[bucket].toLocaleString()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pills / badges
// ---------------------------------------------------------------------------

function LeanPill({ lean }: { lean: CandidateLean }) {
  const meta = LEAN_META[lean];
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded border text-[11px] font-bold uppercase tracking-wide whitespace-nowrap ${
        meta?.pill ?? 'bg-bg-elevated border-border-base text-fg-secondary'
      }`}
    >
      {leanLabel(lean)}
    </span>
  );
}

function SourceBadge({ source }: { source: LeanSource }) {
  const meta = SOURCE_META[source];
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded border text-[11px] whitespace-nowrap ${
        meta?.cls ?? 'bg-bg-elevated border-border-base text-fg-muted'
      }`}
    >
      {meta?.label ?? source}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Table (desktop) / cards (mobile)
// ---------------------------------------------------------------------------

function LeanTable({ rows }: { rows: CandidateLeanRow[] }) {
  return (
    <div className="bg-bg-raised rounded-xl border border-border-base overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-base text-left text-fg-muted">
              <th className="py-2.5 px-4 font-semibold">Voter</th>
              <th className="py-2.5 px-4 font-semibold">Address</th>
              <th className="py-2.5 px-4 font-semibold">Precinct</th>
              <th className="py-2.5 px-4 font-semibold">Response</th>
              <th className="py-2.5 px-4 font-semibold">Source</th>
              <th className="py-2.5 px-4 font-semibold text-right">Recorded</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.knock_response_id}
                className="border-b border-border-base last:border-0 hover:bg-bg-base/40"
              >
                <td className="py-2.5 px-4 text-fg-primary font-medium whitespace-nowrap">
                  {voterName(row)}
                </td>
                <td className="py-2.5 px-4 text-fg-secondary">
                  {row.voter.address || '—'}
                </td>
                <td className="py-2.5 px-4 text-fg-secondary tabular-nums whitespace-nowrap">
                  {row.voter.precinct_num || '—'}
                </td>
                <td className="py-2.5 px-4">
                  <LeanPill lean={row.lean} />
                </td>
                <td className="py-2.5 px-4">
                  <SourceBadge source={row.source} />
                </td>
                <td className="py-2.5 px-4 text-right text-fg-muted text-xs tabular-nums whitespace-nowrap">
                  {formatWhen(row.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LeanCards({ rows }: { rows: CandidateLeanRow[] }) {
  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <div
          key={row.knock_response_id}
          className="rounded-xl border border-border-base bg-bg-raised p-4"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-fg-primary font-semibold truncate">
                {voterName(row)}
              </div>
              {row.voter.address && (
                <div className="text-fg-secondary text-sm mt-0.5 flex items-center gap-1">
                  <MapPin
                    className="w-3.5 h-3.5 text-fg-muted flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span className="truncate">{row.voter.address}</span>
                </div>
              )}
              {row.voter.precinct_num && (
                <div className="text-fg-muted text-xs mt-0.5">
                  Precinct {row.voter.precinct_num}
                </div>
              )}
            </div>
            <LeanPill lean={row.lean} />
          </div>
          <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-border-base">
            <SourceBadge source={row.source} />
            <span className="text-fg-muted text-xs tabular-nums">
              {formatWhen(row.created_at)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default MyResponsesView;
